import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";

import { apiErrorMessage } from "../services/api";
import { formatDateTime } from "../services/datetime";
import { fetchNotice } from "../services/noticeService";

const COUNT_TILES = [
  "total",
  "delivered",
  "opened",
  "rescheduled",
  "cancelled",
  "acknowledged",
  "no_action",
  "unreached",
];
const ACTIONS = ["none", "rescheduled", "cancelled", "acknowledged"];

/** One notice: the message, delivery / open / response counts, and each booking it reached. */
export default function NoticeDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [reload, setReload] = useState(0);
  const [state, setState] = useState({ loading: true, notice: null, recipients: [], meta: null, error: "" });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: "" }));
    fetchNotice(id, { page, action })
      .then(
        (body) =>
          !cancelled &&
          setState({
            loading: false,
            notice: body.data.notice,
            recipients: body.data.recipients,
            meta: body.meta,
            error: "",
          })
      )
      .catch(
        (err) =>
          !cancelled &&
          setState({
            loading: false,
            notice: null,
            recipients: [],
            meta: null,
            error: apiErrorMessage(err, t("notices.loadFailed")),
          })
      );
    return () => {
      cancelled = true;
    };
  }, [id, page, action, reload, i18n.language, t]);

  const { loading, notice, recipients, meta, error } = state;
  const sent = location.state?.sent;
  const skipped = location.state?.skipped ?? 0;

  return (
    <div className="mx-auto max-w-6xl">
      <Link
        to="/notices"
        className="mb-2 inline-flex items-center gap-1 text-dense text-ink-2 hover:text-ink"
      >
        <ArrowLeftIcon className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
        {t("notices.title")}
      </Link>

      {sent && (
        <div
          className="mb-3 rounded-lg border border-accent-line bg-accent-wash px-3 py-2 text-sm text-accent-text"
          role="status"
        >
          {t("notices.sentBanner")}
          {skipped > 0 && <span className="block">{t("notices.skipped", { count: skipped })}</span>}
        </div>
      )}

      {error && (
        <div className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      {loading && !notice ? (
        <div className="h-40 animate-pulse rounded-xl bg-raised" />
      ) : notice ? (
        <>
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold">{notice.type_label}</h1>
              <p className="text-dense text-ink-2">
                {notice.branches.map((b) => b.name).join(", ")} ·{" "}
                {formatDateTime(notice.sent_at ?? notice.created_at, i18n.language)} ·{" "}
                {notice.created_by?.name ?? "—"} · {t(`notices.status.${notice.status}`)}
              </p>
              <p className="text-caption text-ink-2">
                {t("notices.fields.channels")}:{" "}
                {notice.channels.map((c) => t(`notices.channel.${c}`)).join(" · ")}
                {notice.delay_minutes ? ` · ${t("notices.delay", { count: notice.delay_minutes })}` : ""}
              </p>
            </div>
            <button type="button" className="btn-secondary h-9 px-4" onClick={() => setReload((n) => n + 1)}>
              {t("notices.refresh")}
            </button>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
            {COUNT_TILES.map((key) => (
              <div key={key} className="card p-3">
                <p className="text-caption text-ink-2">{t(`notices.counts.${key}`)}</p>
                <p className="text-lg font-semibold">{notice.counts[key] ?? 0}</p>
              </div>
            ))}
          </div>

          <div className="mb-4 grid gap-3 md:grid-cols-2">
            {["ar", "en"].map((l) => (
              <div key={l} className="card p-4" dir={l === "ar" ? "rtl" : "ltr"} lang={l}>
                <p className="text-caption text-ink-2">{t(`notices.lang.${l}`)}</p>
                <p className="font-semibold">{notice.message[`title_${l}`]}</p>
                <p className="whitespace-pre-line text-dense">{notice.message[`body_${l}`]}</p>
              </div>
            ))}
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-2">
            <select
              className="field-input h-9 w-auto py-0"
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setPage(1);
              }}
              aria-label={t("notices.fields.response")}
            >
              <option value="">{t("notices.allResponses")}</option>
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {t(`notices.response.${a}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="card overflow-x-auto">
            <table className="w-full text-dense">
              <thead>
                <tr className="border-b border-line bg-raised text-caption text-ink-2">
                  <th className="px-4 py-2 text-start">{t("notices.preview.when")}</th>
                  <th className="px-4 py-2 text-start">{t("notices.preview.client")}</th>
                  <th className="px-4 py-2 text-start">{t("notices.preview.branch")}</th>
                  <th className="px-4 py-2 text-start">{t("notices.fields.delivery")}</th>
                  <th className="px-4 py-2 text-start">{t("notices.counts.opened")}</th>
                  <th className="px-4 py-2 text-start">{t("notices.fields.response")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {recipients.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2">
                      {r.appointment ? (
                        <Link
                          to={`/appointments/${r.appointment.id}`}
                          className="text-accent-text hover:underline"
                        >
                          <span dir="ltr">{r.appointment.local_starts_at}</span>
                        </Link>
                      ) : (
                        "—"
                      )}
                      <span className="block text-caption text-ink-muted">
                        #{r.appointment?.reference} ·{" "}
                        {r.appointment
                          ? t(`notices.bookingStatus.${r.appointment.status}`, r.appointment.status)
                          : ""}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {r.client?.name ?? "—"}
                      {!r.has_app && (
                        <span className="block text-caption text-ink-2">{t("notices.noApp")}</span>
                      )}
                    </td>
                    <td className="px-4 py-2">{r.appointment?.branch?.name}</td>
                    <td className="px-4 py-2">
                      <span className="block">
                        {t("notices.channel.push")}:{" "}
                        {r.push_status ? t(`notices.delivery.${r.push_status}`, r.push_status) : "—"}
                      </span>
                      {notice.channels.includes("email") && (
                        <span className="block text-caption text-ink-2">
                          {t("notices.channel.email")}:{" "}
                          {r.email_status ? t(`notices.delivery.${r.email_status}`, r.email_status) : "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {r.opened_at ? formatDateTime(r.opened_at, i18n.language) : "—"}
                    </td>
                    <td className="px-4 py-2">
                      {t(`notices.response.${r.action}`)}
                      {r.action_at && (
                        <span className="block text-caption text-ink-2">
                          {formatDateTime(r.action_at, i18n.language)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {meta && meta.last_page > 1 && (
              <div className="flex items-center justify-between border-t border-line px-4 py-3 text-dense text-ink-2">
                <span>{t("notices.bookingsCount", { count: meta.total })}</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary h-8 px-3"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    {t("notices.previous")}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary h-8 px-3"
                    disabled={page >= meta.last_page}
                    onClick={() => setPage(page + 1)}
                  >
                    {t("notices.next")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
